import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { ApiResponse } from '../types';

export interface ApiErrorDetail {
  field?: string;
  message: string;
}

export class ApiError extends Error {
  status?: number;
  details?: ApiErrorDetail[];
  raw?: unknown;
  code?: string; // Preserve original error code (e.g., 'ECONNABORTED' for timeout)
  isNetworkError?: boolean; // Flag for network errors
  isTimeout?: boolean; // Flag for timeout errors

  constructor(message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

const humanizeSegment = (segment: string): string => {
  if (!segment) {
    return '';
  }

  const cleaned = segment
    .replace(/[_-]/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .trim();

  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
};

const humanizeFieldPath = (path: string): string => {
  if (!path) return '';

  const parts = path
    .replace(/\]/g, '')
    .split(/[.[\]]/)
    .filter(Boolean)
    .map((part) => {
      if (/^\d+$/.test(part)) {
        const index = Number(part) + 1;
        return `Item ${index}`;
      }
      return humanizeSegment(part);
    });

  return parts.join(' › ');
};

const formatValidationMessage = (message: string): string => {
  if (!message) return 'Please review your entries and try again.';

  return message.replace(/"([^"]+)"/g, (_, fieldPath) => humanizeFieldPath(fieldPath));
};

class ApiService {
  private api: AxiosInstance;

  constructor() {
    // Determine base URL based on environment
    const getBaseURL = () => {
      // Check for explicit env var first (highest priority)
      // This allows Railway to configure the backend service URL
      const envUrl = process.env.REACT_APP_API_URL;
      if (envUrl) {
        // Ensure it ends with /api if it doesn't already
        const cleanUrl = envUrl.trim();
        if (cleanUrl && !cleanUrl.includes('localhost')) {
          // For production, use the env var as-is (should be full URL like https://server.up.railway.app/api)
          return cleanUrl.endsWith('/api') ? cleanUrl : `${cleanUrl}/api`;
        }
      }
      
      // In production (Railway), check if we should use relative URL
      // Only use relative if no explicit API URL is set AND frontend/backend are on same domain
      if (typeof window !== 'undefined') {
        const hostname = window.location.hostname;
        
        // If on Railway production domain and no explicit API URL, use relative URL
        // (This assumes frontend and backend are on same Railway domain)
        if ((hostname.includes('railway.app') || hostname.includes('up.railway.app')) && !envUrl) {
          return '/api';
        }
        
        // If on ngrok domain, use relative URL
        if (hostname.includes('ngrok')) {
          return '/api';
        }
      }
      
      // Default to localhost for local development
      return 'http://localhost:5001/api';
    };

    const baseURL = getBaseURL();
    
    // Log the base URL for debugging (only in browser)
    if (typeof window !== 'undefined') {
      console.log('[API-SERVICE] Initialized with baseURL:', baseURL, {
        hostname: window.location.hostname,
        fullUrl: window.location.href,
        envUrl: process.env.REACT_APP_API_URL
      });
    }
    
    this.api = axios.create({
      baseURL: baseURL,
      timeout: 30000, // 30 seconds - helps with slow networks and Railway cold starts
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        // Let axios set multipart/form-data when sending FormData
        if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
          delete config.headers['Content-Type'];
        }
        // Log API requests for debugging (only in browser)
        if (typeof window !== 'undefined' && config.url) {
          console.log('[API-SERVICE] Making request:', {
            method: config.method?.toUpperCase(),
            url: config.url,
            baseURL: config.baseURL,
            fullUrl: `${config.baseURL}${config.url}`,
            hasToken: !!token
          });
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle errors
    this.api.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      (error) => {
        // Enhanced error logging
        if (typeof window !== 'undefined') {
          console.error('[API-SERVICE] Request failed:', {
            url: error.config?.url,
            method: error.config?.method?.toUpperCase(),
            baseURL: error.config?.baseURL,
            status: error.response?.status,
            statusText: error.response?.statusText,
            error: error.response?.data?.error || error.message,
            message: error.response?.data?.message,
            isNetworkError: !error.response && error.request,
            corsError: error.message?.includes('CORS') || error.message?.includes('Access-Control')
          });
        }
        
        if (error.response?.status === 401) {
          // Only redirect to login if it's a token expiration or invalid token error
          const errorMessage = error.response?.data?.error || error.response?.data?.message || '';
          const isTokenError = errorMessage.toLowerCase().includes('token') || 
                              errorMessage.toLowerCase().includes('unauthorized') ||
                              errorMessage.toLowerCase().includes('expired');
          
          if (isTokenError) {
            console.log('Token expired or invalid, logging out...');
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
  }

  async get<T>(url: string, paramsOrConfig?: any, config?: any): Promise<ApiResponse<T>> {
    try {
      // Handle two calling patterns:
      // 1. get(url, params) - params as second arg
      // 2. get(url, { params }) - config object with params property
      let queryParams: any;
      let requestConfig: any = {};
      
      if (paramsOrConfig && typeof paramsOrConfig === 'object' && !Array.isArray(paramsOrConfig)) {
        if ('params' in paramsOrConfig) {
          // Pattern 2: get(url, { params: {...}, timeout: ... })
          queryParams = paramsOrConfig.params;
          requestConfig = { ...paramsOrConfig };
          delete requestConfig.params; // Remove params from config, we'll add it separately
        } else if (!('timeout' in paramsOrConfig) && !('headers' in paramsOrConfig)) {
          // Pattern 1: get(url, params) - params object without config properties
          queryParams = paramsOrConfig;
          requestConfig = config || {};
        } else {
          // Could be either - assume it's config with params nested
          queryParams = paramsOrConfig.params;
          requestConfig = { ...paramsOrConfig };
          delete requestConfig.params;
        }
      } else {
        queryParams = paramsOrConfig;
        requestConfig = config || {};
      }
      
      // Merge timeout and other config options
      const finalConfig = {
        ...requestConfig,
        params: queryParams,
        timeout: requestConfig.timeout || this.api.defaults.timeout
      };
      
      // Log the request config for debugging
      if (typeof window !== 'undefined' && queryParams) {
        console.log('[API-SERVICE] GET request:', {
          url,
          queryParams,
          finalConfigParams: finalConfig.params
        });
      }
      
      const response = await this.api.get(url, finalConfig);
      return response.data as any;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async patch<T>(url: string, data?: any, config?: any): Promise<ApiResponse<T>> {
    try {
      const requestConfig = {
        ...config,
      };
      const response = await this.api.patch<ApiResponse<T>>(url, data, requestConfig);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async post<T>(url: string, data?: any, config?: any): Promise<ApiResponse<T>> {
    try {
      // Merge timeout from config if provided, otherwise use default
      const requestConfig = {
        ...config,
        timeout: config?.timeout || this.api.defaults.timeout
      };
      const response = await this.api.post(url, data, requestConfig);
      return response.data as any;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async put<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    try {
      const response = await this.api.put(url, data);
      return response.data as any;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async delete<T>(url: string, config?: { data?: any }): Promise<ApiResponse<T>> {
    try {
      // Support data in DELETE requests (e.g., for password confirmation)
      const response = await this.api.delete(url, config);
      return response.data as any;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  private handleError(error: any): ApiError {
    if (error.response) {
      // Server responded with error status
      const data = error.response.data as any;
      const message = formatValidationMessage(data?.message || data?.error || 'An error occurred');

      const apiError = new ApiError(message);
      apiError.status = error.response.status;

      if (Array.isArray(data?.details)) {
        apiError.details = data.details.map((detail: any) => ({
          field: detail.field,
          message: formatValidationMessage(detail.message),
        }));
      }

      apiError.raw = data;

      return apiError;
    } else if (error.request) {
      // Request was made but no response received
      const isTimeout = error.code === 'ECONNABORTED' || error.message?.includes('timeout');
      const errorDetails = {
        message: error.message,
        code: error.code,
        request: {
          url: error.config?.url,
          method: error.config?.method,
          baseURL: error.config?.baseURL,
          fullUrl: error.config?.baseURL ? `${error.config.baseURL}${error.config.url}` : error.config?.url,
        },
        isNetworkError: !error.response,
        isTimeout: isTimeout,
        isCORS: error.message?.includes('CORS') || error.message?.includes('Access-Control'),
      };
      console.error('[API-SERVICE] Network error details:', errorDetails);
      
      const message = isTimeout
        ? 'The request took too long. The action may have completed — try refreshing the page to check.'
        : 'Network error. Please check your connection and try again.';
      const apiError = new ApiError(message);
      apiError.code = error.code; // Preserve original error code
      apiError.isNetworkError = true;
      apiError.isTimeout = isTimeout;
      return apiError;
    } else {
      // Something else happened
      console.error('[API-SERVICE] Unexpected error:', error);
      return new ApiError(error.message || 'An unexpected error occurred');
    }
  }
}

export const apiService = new ApiService();
